/**
 * Solution ALM tools (6 tools)
 * Export, import, publish, and component management via Dataverse actions.
 */

import { randomUUID } from "node:crypto";
import { inflateRawSync } from "node:zlib";
import { constants as fsConstants } from "node:fs";
import { open, realpath } from "node:fs/promises";
import { basename, dirname, extname, isAbsolute, join, resolve } from "node:path";
import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type { DataverseApi, DataverseAsyncPollResult } from "../api/dataverse-api.js";
import { ApiError, ConfirmationRequiredError } from "../utils/errors.js";
import { logger } from "../utils/logger.js";
import { sanitizeErrorMessage } from "../utils/security.js";

// MCP tool-call payloads are capped at 2 MiB. Leave headroom for the JSON-RPC
// envelope and other fields; larger solution packages must use solutionFilePath.
const MAX_INLINE_BASE64_SIZE = 1_500_000;
const MAX_LOCAL_SOLUTION_FILE_SIZE = 75 * 1024 * 1024;
const MAX_EXPORT_RESPONSE_SIZE = 128 * 1024 * 1024;

const asyncProperties = {
  async: {
    type: "boolean",
    description: "Use the Dataverse asynchronous action (default: true)",
  },
  waitForCompletion: {
    type: "boolean",
    description: "Poll the Dataverse system job and return its outcome (default: true)",
  },
  maxWaitSeconds: {
    type: "number",
    minimum: 0,
    maximum: 600,
    description: "Maximum time to poll in this call, from 0 through 600 seconds (default: 60)",
  },
  pollIntervalMs: {
    type: "number",
    minimum: 100,
    maximum: 10000,
    description: "System-job polling interval in milliseconds (default: 2000)",
  },
} as const;

// ============================================================================
// Tool definitions
// ============================================================================

export const exportSolutionTool: Tool = {
  name: "export_solution",
  description:
    "Export a Dataverse solution as a base64-encoded zip, or resume an earlier asynchronous export by export job ID. Supports synchronous export and bounded system-job polling.",
  inputSchema: {
    type: "object",
    properties: {
      solutionName: {
        type: "string",
        description:
          "Unique name of a solution to start exporting; mutually exclusive with exportJobId",
      },
      exportJobId: {
        type: "string",
        description:
          "Export job GUID returned by an earlier asynchronous export. Downloads that existing export instead of starting a new one.",
      },
      asyncOperationId: {
        type: "string",
        description:
          "Optional async-operation GUID returned with exportJobId. When supplied, poll it before downloading the resumed export.",
      },
      managed: {
        type: "boolean",
        description: "Export as managed (true) or unmanaged (false, default)",
      },
      includeSettings: {
        type: "array",
        uniqueItems: true,
        items: {
          type: "string",
          enum: [
            "autonumbering",
            "calendar",
            "customization",
            "emailtracking",
            "externalapplications",
            "general",
            "marketing",
            "outlooksynchronization",
            "relationshiproles",
            "isvconfig",
            "sales",
          ],
        },
        description: "Optional organization settings to include in the solution package",
      },
      outputFilePath: {
        type: "string",
        description:
          "Optional absolute .zip path to write locally. Recommended for packages too large for the MCP response limit.",
      },
      overwriteOutputFile: {
        type: "boolean",
        description: "Set true to overwrite an existing regular file at outputFilePath",
      },
      ...asyncProperties,
    },
    oneOf: [
      { required: ["solutionName"], not: { required: ["exportJobId"] } },
      { required: ["exportJobId"], not: { required: ["solutionName"] } },
    ],
  },
};

export const importSolutionTool: Tool = {
  name: "import_solution",
  description:
    "Import a base64-encoded Dataverse solution zip. This changes the environment and requires confirm=true. Supports asynchronous import and system-job polling.",
  inputSchema: {
    type: "object",
    properties: {
      customizationFile: {
        type: "string",
        maxLength: MAX_INLINE_BASE64_SIZE,
        description:
          "Base64-encoded solution zip for small packages only (max 1.5 MB encoded). Mutually exclusive with solutionFilePath.",
      },
      solutionFilePath: {
        type: "string",
        description:
          "Absolute local path to a .zip solution package (max 75 MiB). Mutually exclusive with customizationFile.",
      },
      overwriteUnmanagedCustomizations: {
        type: "boolean",
        description: "Overwrite unmanaged customizations (default: false)",
      },
      publishWorkflows: {
        type: "boolean",
        description: "Activate classic workflows after import (default: true)",
      },
      activateFlows: {
        type: "boolean",
        description: "Leave imported cloud flows ON. Default false: flows that arrive ON are turned OFF and listed, since a live copy of a production flow firing in the target environment is rarely what an import means.",
      },
      holdingSolution: {
        type: "boolean",
        description: "Import as a holding solution for a later upgrade (default: false)",
      },
      skipProductUpdateDependencies: {
        type: "boolean",
        description: "Skip dependencies marked as product updates (default: false)",
      },
      componentParameters: {
        type: "array",
        maxItems: 500,
        items: { type: "object", additionalProperties: true },
        description:
          "Optional Dataverse import component parameters for connection references and environment variables",
      },
      confirm: { type: "boolean", description: "Set to true to confirm the import" },
      ...asyncProperties,
    },
    oneOf: [{ required: ["customizationFile"] }, { required: ["solutionFilePath"] }],
  },
};

export const publishAllCustomizationsTool: Tool = {
  name: "publish_all_customizations",
  description:
    "Publish every pending Dataverse customization. This can affect live apps and requires confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      confirm: { type: "boolean", description: "Set to true to confirm publishing all changes" },
    },
  },
};

export const addSolutionComponentTool: Tool = {
  name: "add_solution_component",
  description: "Add an existing component to an unmanaged Dataverse solution.",
  inputSchema: {
    type: "object",
    properties: {
      solutionUniqueName: { type: "string", description: "Solution unique name" },
      componentId: { type: "string", description: "Component record ID (GUID)" },
      componentType: {
        type: "number",
        description:
          "Component type code — 1=Table, 29=Workflow/Flow, 300=Canvas App, 10110=Copilot Studio agent. Newer types live above 10000. If you do not know the code, read it off an existing record: query solutioncomponents filtered by objectid eq <that record id> and take its componenttype.",
      },
      addRequiredComponents: {
        type: "boolean",
        description: "Add required dependencies (default: false)",
      },
      doNotIncludeSubcomponents: {
        type: "boolean",
        description: "Exclude subcomponents where Dataverse supports it (default: false)",
      },
      includedComponentSettingsValues: {
        type: "array",
        maxItems: 100,
        items: { type: "string" },
        description: "Optional component setting names to include",
      },
    },
    required: ["solutionUniqueName", "componentId", "componentType"],
  },
};

export const removeSolutionComponentTool: Tool = {
  name: "remove_solution_component",
  description: "Remove a component from an unmanaged Dataverse solution. Requires confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      solutionUniqueName: { type: "string", description: "Solution unique name" },
      componentId: { type: "string", description: "Component record ID (GUID)" },
      componentType: {
        type: "number",
        description:
          "Component type code — must match the one the component was added with (1=Table, 29=Workflow/Flow, 300=Canvas App, 10110=Copilot Studio agent)",
      },
      confirm: { type: "boolean", description: "Set to true to confirm removal" },
    },
    required: ["solutionUniqueName", "componentId", "componentType"],
  },
};

export const createSolutionTool: Tool = {
  name: "create_solution",
  description:
    "Create an unmanaged Dataverse solution to hold components you intend to ship to another environment. Without a publisher, the environment's default publisher is used — note that the publisher decides the customization prefix every component in the solution must carry, so pick it deliberately if the solution is going somewhere else.",
  inputSchema: {
    type: "object",
    properties: {
      uniqueName: {
        type: "string",
        description:
          "Unique name (letters, digits and underscores; no spaces). This is the name every other solution tool takes.",
      },
      displayName: { type: "string", description: "Friendly name shown in the maker portal" },
      publisherUniqueName: {
        type: "string",
        description:
          "Publisher's unique name. Defaults to the environment's default publisher. List publishers by querying the 'publishers' table for uniquename and customizationprefix.",
      },
      version: { type: "string", description: "Four-part version, default 1.0.0.0" },
      description: { type: "string", description: "Optional description" },
    },
    required: ["uniqueName", "displayName"],
  },
};

export const deleteSolutionTool: Tool = {
  name: "delete_solution",
  description:
    "Delete a Dataverse solution. WHAT THIS DOES DEPENDS ON THE SOLUTION TYPE, and the difference is destructive: deleting an UNMANAGED solution removes only the container and leaves every component in place, while deleting a MANAGED solution UNINSTALLS it and DELETES the components it brought with it. Both were verified against a live environment. Requires confirm=true, and reports what will actually happen before it does it.",
  inputSchema: {
    type: "object",
    properties: {
      solutionUniqueName: { type: "string", description: "Solution unique name" },
      confirm: { type: "boolean", description: "Set to true to confirm the deletion" },
    },
    required: ["solutionUniqueName"],
  },
};

export const cloneSolutionTool: Tool = {
  name: "clone_solution",
  description:
    "Consolidate an unmanaged Dataverse solution's patches into a new version. DESPITE THE NAME, THIS DOES NOT PRODUCE A SECOND SOLUTION: it acts IN PLACE on the existing solution — same solution ID, same unique name — and OVERWRITES its display name and version with the ones you pass. Verified against a live environment. If you want an actual copy, export the solution and import it under a different unique name.",
  inputSchema: {
    type: "object",
    properties: {
      parentSolutionUniqueName: {
        type: "string",
        description: "Unique name of the solution to consolidate. This solution is what gets modified.",
      },
      displayName: {
        type: "string",
        description:
          "New display name. This RENAMES the existing solution — it is not the name of a copy. Pass the current name to leave it unchanged.",
      },
      versionNumber: { type: "string", description: "Four-part version (for example, 1.1.0.0)" },
    },
    required: ["parentSolutionUniqueName", "displayName", "versionNumber"],
  },
};

// ============================================================================
// Validation
// ============================================================================

const solutionUniqueName = z
  .string()
  .min(1)
  .max(128)
  .regex(
    /^[A-Za-z][A-Za-z0-9_]*$/,
    "Solution unique name must start with a letter and contain only letters, numbers, and underscores",
  );

const guidString = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    "Component ID must be a GUID",
  );

const asyncSchema = {
  async: z.boolean().optional().default(true),
  waitForCompletion: z.boolean().optional().default(true),
  maxWaitSeconds: z.number().int().min(0).max(600).optional().default(60),
  pollIntervalMs: z.number().int().min(100).max(10_000).optional().default(2_000),
};

const includeSettingSchema = z.enum([
  "autonumbering",
  "calendar",
  "customization",
  "emailtracking",
  "externalapplications",
  "general",
  "marketing",
  "outlooksynchronization",
  "relationshiproles",
  "isvconfig",
  "sales",
]);

const exportSchema = z
  .object({
    solutionName: solutionUniqueName.optional(),
    exportJobId: guidString.optional(),
    asyncOperationId: guidString.optional(),
    managed: z.boolean().optional().default(false),
    includeSettings: z.array(includeSettingSchema).max(11).optional().default([]),
    outputFilePath: z.string().min(1).max(4096).optional(),
    overwriteOutputFile: z.boolean().optional().default(false),
    ...asyncSchema,
  })
  .refine((value) => !value.overwriteOutputFile || value.outputFilePath !== undefined, {
    message: "overwriteOutputFile requires outputFilePath",
    path: ["overwriteOutputFile"],
  })
  .superRefine((value, context) => {
    if ((value.solutionName === undefined) === (value.exportJobId === undefined)) {
      context.addIssue({
        code: "custom",
        message: "Provide exactly one of solutionName or exportJobId",
        path: ["solutionName"],
      });
    }
    if (value.asyncOperationId !== undefined && value.exportJobId === undefined) {
      context.addIssue({
        code: "custom",
        message: "asyncOperationId requires exportJobId",
        path: ["asyncOperationId"],
      });
    }
  });

function hasZipSignature(value: Uint8Array): boolean {
  if (value.length < 4 || value[0] !== 0x50 || value[1] !== 0x4b) return false;
  const signature = `${value[2]}:${value[3]}`;
  return signature === "3:4" || signature === "5:6" || signature === "7:8";
}

function isBase64Zip(value: string): boolean {
  if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) {
    return false;
  }
  const signature = Buffer.from(value.slice(0, 8), "base64");
  return hasZipSignature(signature);
}

const importSchema = z
  .object({
    activateFlows: z
      .boolean()
      .optional()
      .default(false)
      .describe("Leave imported cloud flows turned ON. Default false: flows that arrive ON are turned off and reported, because their connection references usually still need mapping in the target."),
    customizationFile: z
      .string()
      .min(8)
      .max(MAX_INLINE_BASE64_SIZE)
      .refine(isBase64Zip, "Customization file must be a valid base64-encoded ZIP archive")
      .optional(),
    solutionFilePath: z.string().min(1).max(4096).optional(),
    overwriteUnmanagedCustomizations: z.boolean().optional().default(false),
    publishWorkflows: z.boolean().optional().default(true),
    holdingSolution: z.boolean().optional().default(false),
    skipProductUpdateDependencies: z.boolean().optional().default(false),
    componentParameters: z.array(z.record(z.string(), z.unknown())).max(500).optional(),
    confirm: z.boolean().optional().default(false),
    ...asyncSchema,
  })
  .refine(
    (value) => (value.customizationFile === undefined) !== (value.solutionFilePath === undefined),
    {
      message: "Provide exactly one of customizationFile or solutionFilePath",
      path: ["customizationFile"],
    },
  );

const publishSchema = z.object({
  confirm: z.boolean().optional().default(false),
});

/**
 * Solution component types are a platform-defined enum, and it grows. The
 * older values are small (1 = Entity, 29 = Workflow), but newer ones live
 * above 10000 — a Copilot Studio agent is **10110**, verified against a live
 * environment by reading the `solutioncomponent` row of an existing agent.
 *
 * An earlier cap of 10000 rejected exactly that, so this server could not add
 * an agent to a solution at all: a made-up limit on someone else's enum fails
 * closed against real values. The bound is kept only to catch a typo'd or
 * garbage number, so it sits far above anything Microsoft has shipped rather
 * than hugging today's maximum.
 */
const componentTypeNumber = z.number().int().min(0).max(100_000);

const addComponentSchema = z.object({
  solutionUniqueName,
  componentId: guidString,
  componentType: componentTypeNumber,
  addRequiredComponents: z.boolean().optional().default(false),
  doNotIncludeSubcomponents: z.boolean().optional().default(false),
  includedComponentSettingsValues: z.array(z.string().min(1).max(256)).max(100).optional(),
});

const removeComponentSchema = z.object({
  solutionUniqueName,
  componentId: guidString,
  componentType: componentTypeNumber,
  confirm: z.boolean().optional().default(false),
});

const createSolutionSchema = z.object({
  uniqueName: z
    .string()
    .trim()
    .min(1)
    .max(65)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      "Solution unique name must start with a letter or underscore and contain only letters, digits and underscores",
    ),
  displayName: z.string().trim().min(1).max(256),
  publisherUniqueName: z.string().trim().min(1).max(256).optional(),
  version: z
    .string()
    .regex(/^\d+\.\d+\.\d+\.\d+$/, "Version must contain four numeric parts")
    .optional(),
  description: z.string().max(2000).optional(),
});

const deleteSolutionSchema = z.object({
  solutionUniqueName,
  confirm: z.boolean().optional().default(false),
});

const cloneSchema = z.object({
  parentSolutionUniqueName: solutionUniqueName,
  displayName: z.string().trim().min(1).max(256),
  versionNumber: z
    .string()
    .regex(/^\d+\.\d+\.\d+\.\d+$/, "Version must contain four numeric parts")
    .refine(
      (version) => version.split(".").every((part) => Number(part) <= 65535),
      "Each version part must be between 0 and 65535",
    ),
});

// ============================================================================
// Helpers
// ============================================================================

export interface SolutionToolsContext {
  api: DataverseApi | null;
}

/**
 * Dataverse system-job status codes. Reporting a bare "status code 20" makes
 * the reader look up a number that the platform already has a word for.
 */
const ASYNC_STATUS_LABELS: Record<number, string> = {
  0: "Waiting for resources",
  10: "Waiting",
  20: "In progress",
  21: "Pausing",
  22: "Canceling",
  30: "Succeeded",
  31: "Failed",
  32: "Canceled",
};

export function describeAsyncStatus(operation: {
  statuscode?: number;
  message?: string;
  friendlymessage?: string;
}): string {
  const label = operation.statuscode === undefined ? undefined : ASYNC_STATUS_LABELS[operation.statuscode];
  const detail = operation.friendlymessage ?? operation.message;
  if (label !== undefined) return detail ? `${label} — ${detail}` : label;
  if (detail) return detail;
  return operation.statuscode === undefined ? "unknown" : `status code ${operation.statuscode}`;
}

function dataverseUnavailable(): string {
  return (
    "Dataverse is not available for this environment. Configure the environment's " +
    "Dataverse URL and enable the Dataverse feature before using solution ALM tools."
  );
}

const exportSettingsMap: Record<z.infer<typeof includeSettingSchema>, string> = {
  autonumbering: "ExportAutoNumberingSettings",
  calendar: "ExportCalendarSettings",
  customization: "ExportCustomizationSettings",
  emailtracking: "ExportEmailTrackingSettings",
  externalapplications: "ExportExternalApplications",
  general: "ExportGeneralSettings",
  marketing: "ExportMarketingSettings",
  outlooksynchronization: "ExportOutlookSynchronizationSettings",
  relationshiproles: "ExportRelationshipRoles",
  isvconfig: "ExportIsvConfig",
  sales: "ExportSales",
};

function addExportSettings(
  payload: Record<string, unknown>,
  settings: z.infer<typeof includeSettingSchema>[],
): void {
  for (const setting of settings) payload[exportSettingsMap[setting]] = true;
}

function decodedBase64Size(value: string): number {
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return Math.floor((value.length * 3) / 4) - padding;
}

function getExportFile(result: { ExportSolutionFile?: unknown }): string {
  if (
    typeof result.ExportSolutionFile !== "string" ||
    result.ExportSolutionFile.length === 0 ||
    !isBase64Zip(result.ExportSolutionFile)
  ) {
    throw new ApiError("Export solution: Dataverse did not return ExportSolutionFile", 502);
  }
  return result.ExportSolutionFile;
}

async function resolveLocalZipPath(filePath: string): Promise<string> {
  if (!isAbsolute(filePath)) {
    throw new ApiError("Solution file path must be absolute", 400);
  }
  if (resolve(filePath) !== filePath) {
    throw new ApiError("Solution file path must be normalized and cannot contain traversal", 400);
  }
  if (extname(filePath).toLowerCase() !== ".zip") {
    throw new ApiError("Solution file path must use the .zip extension", 400);
  }

  // Resolve the parent instead of the target so a new export path is supported.
  // This also removes `..` segments and avoids writing through a symlinked parent.
  const resolvedParent = await realpath(dirname(filePath));
  return join(resolvedParent, basename(filePath));
}

async function readLocalSolutionZip(filePath: string): Promise<string> {
  const resolvedPath = await resolveLocalZipPath(filePath);
  const handle = await open(resolvedPath, fsConstants.O_RDONLY | fsConstants.O_NOFOLLOW);
  try {
    const fileInfo = await handle.stat();
    if (!fileInfo.isFile()) {
      throw new ApiError("Solution file path must identify a regular file", 400);
    }
    if (fileInfo.size < 4 || fileInfo.size > MAX_LOCAL_SOLUTION_FILE_SIZE) {
      throw new ApiError("Solution ZIP must be between 4 bytes and 75 MiB", 413);
    }
    const contents = await handle.readFile();
    if (!hasZipSignature(contents)) {
      throw new ApiError("Solution file does not have a valid ZIP signature", 400);
    }
    return contents.toString("base64");
  } finally {
    await handle.close();
  }
}

async function writeLocalSolutionZip(
  filePath: string,
  base64File: string,
  overwrite: boolean,
): Promise<string> {
  const resolvedPath = await resolveLocalZipPath(filePath);
  const contents = Buffer.from(base64File, "base64");
  if (!hasZipSignature(contents)) {
    throw new ApiError("Exported solution does not have a valid ZIP signature", 502);
  }
  if (contents.length > MAX_LOCAL_SOLUTION_FILE_SIZE) {
    throw new ApiError("Exported solution exceeds the 75 MiB local file limit", 413);
  }

  const createMode = overwrite ? fsConstants.O_TRUNC : fsConstants.O_EXCL;
  const handle = await open(
    resolvedPath,
    fsConstants.O_WRONLY | fsConstants.O_CREAT | createMode | fsConstants.O_NOFOLLOW,
    0o600,
  );
  try {
    await handle.writeFile(contents);
  } finally {
    await handle.close();
  }
  return resolvedPath;
}

async function formatExportResult(
  heading: string,
  file: string,
  outputFilePath?: string,
  overwriteOutputFile = false,
): Promise<string> {
  const header = [heading, `ZIP size: ${decodedBase64Size(file)} bytes`];
  if (outputFilePath) {
    const writtenPath = await writeLocalSolutionZip(outputFilePath, file, overwriteOutputFile);
    return [...header, `Saved to: ${writtenPath}`].join("\n");
  }
  if (file.length > MAX_INLINE_BASE64_SIZE) {
    throw new ApiError(
      "Exported solution is too large for an MCP response; retry with an absolute outputFilePath",
      413,
    );
  }
  return [...header, `CustomizationFile (base64): ${file}`].join("\n");
}

function assertAsyncSucceeded(result: DataverseAsyncPollResult, operation: string): void {
  if (!result.completed || result.succeeded) return;
  const detail = sanitizeErrorMessage(
    result.operation.friendlymessage ||
      result.operation.message ||
      `${operation} system job ended with status ${result.operation.statuscode}`,
  );
  throw new ApiError(`${operation} failed`, 500, detail);
}

function formatRunningJob(
  operation: string,
  asyncOperationId: string,
  extra: string[] = [],
): string {
  return [
    `${operation} started and is still running.`,
    `Async operation ID: ${asyncOperationId}`,
    ...extra,
    "Use get_dataverse_row with entitySetName=asyncoperations and this ID to check status.",
  ].join("\n");
}

// ============================================================================
// Handlers
// ============================================================================

export async function handleExportSolution(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = exportSchema.parse(input);
  if (!context.api) return dataverseUnavailable();

  if (parsed.exportJobId) {
    logger.info(
      { exportJobId: parsed.exportJobId, asyncOperationId: parsed.asyncOperationId },
      "Resuming export_solution",
    );
    if (parsed.asyncOperationId && parsed.waitForCompletion) {
      const poll = await context.api.waitForAsyncOperation(parsed.asyncOperationId, {
        maxWaitMs: parsed.maxWaitSeconds * 1000,
        pollIntervalMs: parsed.pollIntervalMs,
      });
      assertAsyncSucceeded(poll, "Solution export");
      if (!poll.completed) {
        return formatRunningJob("Solution export", parsed.asyncOperationId, [
          `Export job ID: ${parsed.exportJobId}`,
          `Status: ${describeAsyncStatus(poll.operation)}`,
        ]);
      }
    }

    const downloaded = await context.api.executeUnboundAction<{
      ExportSolutionFile?: unknown;
    }>(
      "DownloadSolutionExportData",
      { ExportJobId: parsed.exportJobId },
      {
        context: "Download asynchronous solution export",
        maxResponseBytes: MAX_EXPORT_RESPONSE_SIZE,
      },
    );
    return await formatExportResult(
      `Downloaded solution export job ${parsed.exportJobId}.`,
      getExportFile(downloaded),
      parsed.outputFilePath,
      parsed.overwriteOutputFile,
    );
  }

  const solutionName = parsed.solutionName!;
  logger.info(
    { solutionName, managed: parsed.managed, async: parsed.async },
    "Executing export_solution",
  );

  const payload: Record<string, unknown> = {
    SolutionName: solutionName,
    Managed: parsed.managed,
  };
  addExportSettings(payload, parsed.includeSettings);

  if (!parsed.async) {
    const result = await context.api.executeUnboundAction<{ ExportSolutionFile?: unknown }>(
      "ExportSolution",
      payload,
      { context: "Export solution", maxResponseBytes: MAX_EXPORT_RESPONSE_SIZE },
    );
    return await formatExportResult(
      `Exported solution "${solutionName}" (${parsed.managed ? "managed" : "unmanaged"}).`,
      getExportFile(result),
      parsed.outputFilePath,
      parsed.overwriteOutputFile,
    );
  }

  const started = await context.api.executeUnboundAction<{
    AsyncOperationId?: string;
    ExportJobId?: string;
  }>("ExportSolutionAsync", payload, { context: "Start asynchronous solution export" });
  if (!started.AsyncOperationId || !started.ExportJobId) {
    throw new ApiError(
      "Export solution: Dataverse did not return AsyncOperationId and ExportJobId",
      502,
    );
  }

  if (!parsed.waitForCompletion) {
    return formatRunningJob("Solution export", started.AsyncOperationId, [
      `Export job ID: ${started.ExportJobId}`,
    ]);
  }

  const poll = await context.api.waitForAsyncOperation(started.AsyncOperationId, {
    maxWaitMs: parsed.maxWaitSeconds * 1000,
    pollIntervalMs: parsed.pollIntervalMs,
  });
  assertAsyncSucceeded(poll, "Solution export");
  if (!poll.completed) {
    return formatRunningJob("Solution export", started.AsyncOperationId, [
      `Export job ID: ${started.ExportJobId}`,
      `Status: ${describeAsyncStatus(poll.operation)}`,
    ]);
  }

  const downloaded = await context.api.executeUnboundAction<{
    ExportSolutionFile?: unknown;
  }>(
    "DownloadSolutionExportData",
    { ExportJobId: started.ExportJobId },
    {
      context: "Download asynchronous solution export",
      maxResponseBytes: MAX_EXPORT_RESPONSE_SIZE,
    },
  );
  return await formatExportResult(
    `Exported solution "${solutionName}" (${parsed.managed ? "managed" : "unmanaged"}).`,
    getExportFile(downloaded),
    parsed.outputFilePath,
    parsed.overwriteOutputFile,
  );
}

/**
 * The solution's UniqueName, read straight out of the package's solution.xml.
 * Parses the ZIP central directory itself (stored and deflated entries) so no
 * archive dependency is needed; returns null if anything is unexpected.
 */
export function solutionUniqueNameFromZip(base64: string): string | null {
  try {
    const buf = Buffer.from(base64, "base64");
    // End of central directory record (0x06054b50) sits within the last 64KB.
    const start = Math.max(0, buf.length - 65557);
    for (let i = buf.length - 22; i >= start; i -= 1) {
      if (buf.readUInt32LE(i) !== 0x06054b50) continue;
      let cd = buf.readUInt32LE(i + 16);
      const count = buf.readUInt16LE(i + 10);
      for (let n = 0; n < count && cd + 46 <= buf.length; n += 1) {
        if (buf.readUInt32LE(cd) !== 0x02014b50) return null;
        const method = buf.readUInt16LE(cd + 10);
        const compressedSize = buf.readUInt32LE(cd + 20);
        const nameLength = buf.readUInt16LE(cd + 28);
        const extraLength = buf.readUInt16LE(cd + 30);
        const commentLength = buf.readUInt16LE(cd + 32);
        const localHeader = buf.readUInt32LE(cd + 42);
        const name = buf.subarray(cd + 46, cd + 46 + nameLength).toString("utf8");
        if (name.toLowerCase() === "solution.xml") {
          const lhName = buf.readUInt16LE(localHeader + 26);
          const lhExtra = buf.readUInt16LE(localHeader + 28);
          const dataStart = localHeader + 30 + lhName + lhExtra;
          const data = buf.subarray(dataStart, dataStart + compressedSize);
          const xml = (method === 8 ? inflateRawSync(data) : data).toString("utf8");
          return /<UniqueName>\s*([^<\s]+)\s*<\/UniqueName>/.exec(xml)?.[1] ?? null;
        }
        cd += 46 + nameLength + extraLength + commentLength;
      }
      return null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * After an import, cloud flows in the solution can arrive turned ON — seen
 * live: a copy of a production Jira intake flow imported into the Sandbox
 * came up Started, with the Sandbox's own connections available to it. Unless
 * the caller asked for that, turn them off and say so; connection references
 * usually still need mapping before such a flow should run.
 */
async function settleImportedFlows(
  api: DataverseApi,
  customizationFile: string,
  activateFlows: boolean,
): Promise<string[]> {
  const uniqueName = solutionUniqueNameFromZip(customizationFile);
  if (!uniqueName) {
    return ["Could not read the solution's UniqueName from the package, so its flows were not checked — use list_solution_flows and toggle_flow."];
  }
  try {
    const solutions = (await api.queryRows("solutions", {
      select: "solutionid,friendlyname",
      filter: `uniquename eq '${uniqueName.replace(/'/g, "''")}'`,
      top: 1,
    })).value;
    const solutionId = solutions[0]?.["solutionid"];
    if (typeof solutionId !== "string") return [`Solution ${uniqueName} was not found after import; its flows were not checked.`];
    const flows = await api.listSolutionFlows(solutionId);
    if (flows.length === 0) return [`Solution ${uniqueName}: no cloud flows.`];
    const rows = flows.map((f) => f as unknown as Record<string, unknown>);
    const on = rows.filter((r) => Number(r["statecode"]) === 1);
    const lines = [`Solution ${uniqueName}: ${flows.length} cloud flow${flows.length === 1 ? "" : "s"}.`];
    if (on.length === 0) {
      lines.push("  All arrived turned off. Map their connection references, then start them with toggle_flow.");
      return lines;
    }
    if (activateFlows) {
      lines.push(`  ${on.length} arrived ON and ${on.length === 1 ? "was" : "were"} left on (activateFlows=true): ${on.map((r) => String(r["name"])).join(", ")}`);
      return lines;
    }
    const stopped: string[] = [];
    const failed: string[] = [];
    for (const row of on) {
      const id = String(row["workflowid"] ?? "");
      try {
        await api.updateRow("workflows", id, { statecode: 0, statuscode: 1 });
        stopped.push(String(row["name"] ?? id));
      } catch (err) {
        failed.push(`${String(row["name"] ?? id)} (${err instanceof Error ? err.message : String(err)})`);
      }
    }
    if (stopped.length > 0) {
      lines.push(`  ${stopped.length} arrived ON and ${stopped.length === 1 ? "was" : "were"} turned OFF: ${stopped.join(", ")}. Map connection references, then toggle_flow to start; pass activateFlows=true to keep imported flows on.`);
    }
    if (failed.length > 0) {
      lines.push(`  ⚠️ Could not turn off: ${failed.join("; ")} — these flows are LIVE in this environment; stop them with toggle_flow now.`);
    }
    return lines;
  } catch (err) {
    return [`⚠️ Could not check the imported flows' state (${err instanceof Error ? err.message : String(err)}) — run list_solution_flows and toggle_flow; imported flows may be ON.`];
  }
}

export async function handleImportSolution(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = importSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError([
      "Solution import not confirmed. This changes Dataverse components in the current environment.",
      `Overwrite unmanaged customizations: ${parsed.overwriteUnmanagedCustomizations}`,
      `Import as holding solution: ${parsed.holdingSolution}`,
      "Set confirm=true to proceed.",
    ].join("\n"));
  }
  if (!context.api) return dataverseUnavailable();
  logger.info(
    {
      async: parsed.async,
      overwriteUnmanagedCustomizations: parsed.overwriteUnmanagedCustomizations,
      holdingSolution: parsed.holdingSolution,
    },
    "Executing import_solution",
  );

  const customizationFile =
    parsed.customizationFile ?? (await readLocalSolutionZip(parsed.solutionFilePath!));
  const importJobId = randomUUID();
  const payload: Record<string, unknown> = {
    OverwriteUnmanagedCustomizations: parsed.overwriteUnmanagedCustomizations,
    PublishWorkflows: parsed.publishWorkflows,
    CustomizationFile: customizationFile,
    ImportJobId: importJobId,
    HoldingSolution: parsed.holdingSolution,
    SkipProductUpdateDependencies: parsed.skipProductUpdateDependencies,
  };
  // ComponentParameters is an EntityCollection. Sending an EMPTY array is not
  // the same as omitting it: Dataverse tries to deserialize the untyped
  // collection and fails the whole import with
  // "0x80040216: Custom state is not set for entity type crmbaseentity".
  // That made import_solution fail for EVERY solution, empty ones included.
  if (parsed.componentParameters !== undefined && parsed.componentParameters.length > 0) {
    payload.ComponentParameters = parsed.componentParameters;
  }

  if (!parsed.async) {
    await context.api.executeUnboundAction("ImportSolution", payload, {
      context: "Import solution",
    });
    const flowLines = await settleImportedFlows(context.api, customizationFile, parsed.activateFlows);
    return [`Imported solution successfully.`, `Import job ID: ${importJobId}`, ...flowLines].join("\n");
  }

  const started = await context.api.executeUnboundAction<{
    AsyncOperationId?: string;
    ImportJobKey?: string;
  }>("ImportSolutionAsync", payload, { context: "Start asynchronous solution import" });
  if (!started.AsyncOperationId) {
    throw new ApiError("Import solution: Dataverse did not return AsyncOperationId", 502);
  }

  const jobDetails = [
    `Import job ID: ${importJobId}`,
    ...(started.ImportJobKey ? [`Import job key: ${started.ImportJobKey}`] : []),
  ];
  if (!parsed.waitForCompletion) {
    return formatRunningJob("Solution import", started.AsyncOperationId, jobDetails);
  }

  const poll = await context.api.waitForAsyncOperation(started.AsyncOperationId, {
    maxWaitMs: parsed.maxWaitSeconds * 1000,
    pollIntervalMs: parsed.pollIntervalMs,
  });
  assertAsyncSucceeded(poll, "Solution import");
  if (!poll.completed) {
    return formatRunningJob("Solution import", started.AsyncOperationId, [
      ...jobDetails,
      `Status: ${describeAsyncStatus(poll.operation)}`,
    ]);
  }
  const flowLines = await settleImportedFlows(context.api, customizationFile, parsed.activateFlows);
  return [
    "Imported solution successfully.",
    `Async operation ID: ${started.AsyncOperationId}`,
    ...jobDetails,
    ...flowLines,
  ].join("\n");
}

export async function handlePublishAllCustomizations(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = publishSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError((
      "Publishing not confirmed. This publishes every pending customization in the current " +
      "Dataverse environment and can affect live apps. Set confirm=true to proceed."
    ));
  }
  if (!context.api) return dataverseUnavailable();
  logger.info("Executing publish_all_customizations");
  await context.api.executeUnboundAction(
    "PublishAllXml",
    {},
    {
      context: "Publish all customizations",
    },
  );
  return "Published all Dataverse customizations successfully.";
}

export async function handleCreateSolution(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = createSolutionSchema.parse(input);
  if (!context.api) return dataverseUnavailable();
  logger.info({ solution: parsed.uniqueName }, "Executing create_solution");

  // Resolve the publisher first. Creating the solution and THEN discovering
  // the publisher was wrong would leave a half-made solution behind.
  let publisherId: string | undefined;
  let publisherPrefix: string | undefined;
  if (parsed.publisherUniqueName === undefined) {
    const defaults = await context.api.queryRows("publishers", {
      select: "publisherid,uniquename,customizationprefix,isreadonly",
      filter: "isreadonly eq false",
      top: 50,
    });
    // The default publisher is the one whose prefix real components already
    // use. Prefer a non-Microsoft one; "new" is the stock fallback prefix.
    const candidates = defaults.value.filter((row) => {
      const prefix = row["customizationprefix"];
      return typeof prefix === "string" && prefix !== "";
    });
    const chosen = candidates.find((row) => row["customizationprefix"] !== "new") ?? candidates[0];
    if (chosen === undefined) {
      throw new ApiError(
        "No usable publisher was found in this environment, so the solution cannot be created. Pass publisherUniqueName explicitly.",
        404,
      );
    }
    publisherId = String(chosen["publisherid"]);
    publisherPrefix = String(chosen["customizationprefix"]);
  } else {
    const found = await context.api.queryRows("publishers", {
      select: "publisherid,customizationprefix",
      filter: `uniquename eq '${parsed.publisherUniqueName.replace(/'/g, "''")}'`,
      top: 1,
    });
    const row = found.value[0];
    if (row === undefined) {
      throw new ApiError(
        `No publisher named '${parsed.publisherUniqueName}' exists in this environment. Query the 'publishers' table for uniquename to see what is available.`,
        404,
      );
    }
    publisherId = String(row["publisherid"]);
    publisherPrefix = String(row["customizationprefix"] ?? "");
  }

  const created = await context.api.createRow("solutions", {
    uniquename: parsed.uniqueName,
    friendlyname: parsed.displayName,
    version: parsed.version ?? "1.0.0.0",
    ...(parsed.description !== undefined ? { description: parsed.description } : {}),
    "publisherid@odata.bind": `/publishers(${publisherId})`,
  });

  const solutionId = created["solutionid"];
  return [
    `Created unmanaged solution "${parsed.displayName}" (${parsed.uniqueName}).`,
    ...(solutionId !== undefined ? [`Solution ID: ${String(solutionId)}`] : []),
    `Publisher prefix: ${publisherPrefix === "" ? "(none)" : publisherPrefix} — every component added to this solution carries it.`,
    "",
    "Add components with add_solution_component, then export_solution to package it.",
  ].join("\n");
}

export async function handleDeleteSolution(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = deleteSolutionSchema.parse(input);
  if (!context.api) return dataverseUnavailable();

  const found = await context.api.queryRows("solutions", {
    select: "solutionid,friendlyname,ismanaged,version",
    filter: `uniquename eq '${parsed.solutionUniqueName.replace(/'/g, "''")}'`,
    top: 1,
  });
  const solution = found.value[0];
  if (solution === undefined) {
    throw new ApiError(`No solution named '${parsed.solutionUniqueName}' exists in this environment.`, 404);
  }
  const solutionId = String(solution["solutionid"]);
  const isManaged = solution["ismanaged"] === true;
  const displayName = String(solution["friendlyname"] ?? parsed.solutionUniqueName);

  // Count what is inside, because for a managed solution this is the number
  // of components that are about to be deleted along with it.
  let componentCount: number | undefined;
  try {
    const components = await context.api.queryRows("solutioncomponents", {
      select: "solutioncomponentid",
      filter: `_solutionid_value eq ${solutionId}`,
      top: 5000,
    });
    componentCount = components.value.length;
  } catch {
    // Not being able to count is not a reason to refuse, but it IS a reason
    // not to claim a number.
    componentCount = undefined;
  }

  const consequence = isManaged
    ? [
        `This solution is MANAGED. Deleting it UNINSTALLS it and DELETES the components it installed${
          componentCount === undefined ? "" : ` — ${componentCount} of them`
        }.`,
        "Anything those components hold goes with them. There is no undo short of re-importing the package.",
      ]
    : [
        `This solution is UNMANAGED. Deleting it removes only the container${
          componentCount === undefined ? "" : `; its ${componentCount} component(s) stay in the environment`
        }.`,
      ];

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(
      [`Deletion of solution "${displayName}" (${parsed.solutionUniqueName}) not confirmed.`, ...consequence, "Set confirm=true to proceed."].join("\n"),
    );
  }

  logger.info(
    { solution: parsed.solutionUniqueName, managed: isManaged, components: componentCount },
    "Executing delete_solution",
  );
  await context.api.deleteRow("solutions", solutionId);

  return [
    `Deleted solution "${displayName}" (${parsed.solutionUniqueName}).`,
    ...consequence,
  ].join("\n");
}

export async function handleAddSolutionComponent(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = addComponentSchema.parse(input);
  if (!context.api) return dataverseUnavailable();
  logger.info(
    { solution: parsed.solutionUniqueName, component: parsed.componentId },
    "Executing add_solution_component",
  );

  const payload: Record<string, unknown> = {
    SolutionUniqueName: parsed.solutionUniqueName,
    ComponentId: parsed.componentId,
    ComponentType: parsed.componentType,
    AddRequiredComponents: parsed.addRequiredComponents,
    DoNotIncludeSubcomponents: parsed.doNotIncludeSubcomponents,
  };
  if (parsed.includedComponentSettingsValues) {
    payload.IncludedComponentSettingsValues = parsed.includedComponentSettingsValues;
  }
  const result = await context.api.executeUnboundAction<{ id?: string }>(
    "AddSolutionComponent",
    payload,
    { context: "Add solution component" },
  );

  return [
    `Added component ${parsed.componentId} (type ${parsed.componentType}) to solution ${parsed.solutionUniqueName}.`,
    ...(result.id ? [`Solution component ID: ${result.id}`] : []),
  ].join("\n");
}

export async function handleRemoveSolutionComponent(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = removeComponentSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError((
      `Removal not confirmed. Set confirm=true to remove component ${parsed.componentId} ` +
      `from solution ${parsed.solutionUniqueName}.`
    ));
  }
  if (!context.api) return dataverseUnavailable();
  logger.info(
    { solution: parsed.solutionUniqueName, component: parsed.componentId },
    "Executing remove_solution_component",
  );

  await context.api.executeUnboundAction(
    "RemoveSolutionComponent",
    {
      SolutionComponent: {
        "@odata.type": "Microsoft.Dynamics.CRM.solutioncomponent",
        solutioncomponentid: parsed.componentId,
      },
      ComponentType: parsed.componentType,
      SolutionUniqueName: parsed.solutionUniqueName,
    },
    { context: "Remove solution component" },
  );
  return `Removed component ${parsed.componentId} from solution ${parsed.solutionUniqueName}.`;
}

export async function handleCloneSolution(
  context: SolutionToolsContext,
  input: unknown,
): Promise<string> {
  const parsed = cloneSchema.parse(input);
  if (!context.api) return dataverseUnavailable();
  logger.info({ parent: parsed.parentSolutionUniqueName }, "Executing clone_solution");

  const result = await context.api.executeUnboundAction<{ SolutionId?: string }>(
    "CloneAsSolution",
    {
      ParentSolutionUniqueName: parsed.parentSolutionUniqueName,
      DisplayName: parsed.displayName,
      VersionNumber: parsed.versionNumber,
    },
    { context: "Clone solution" },
  );
  return [
    `Consolidated solution ${parsed.parentSolutionUniqueName} into version ${parsed.versionNumber}.`,
    `Its display name is now "${parsed.displayName}".`,
    "This changed that solution in place — no second solution was created.",
    ...(result.SolutionId ? [`Solution ID: ${result.SolutionId}`] : []),
  ].join("\n");
}

export const solutionTools: Tool[] = [
  createSolutionTool,
  deleteSolutionTool,
  exportSolutionTool,
  importSolutionTool,
  publishAllCustomizationsTool,
  addSolutionComponentTool,
  removeSolutionComponentTool,
  cloneSolutionTool,
];
